import { useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCampaignStore } from '@/stores';
import { useMyCampaigns } from '@/hooks/queries';

// TanStack Router's navigate is strongly typed against the route tree.
// For the layout-level "clean the URL in place" idiom (no `to:`, just a
// search updater) its inferred search schema collapses to `never`, so we
// call navigate through a looser signature with the updater cast. The
// runtime behavior is unchanged — only the types are relaxed.
type LooseNavigate = (opts: {
  to?: string;
  search?: unknown;
  replace?: boolean;
}) => unknown;

const STRIP_CAMPAIGN_ID = (prev: Record<string, unknown>) => ({
  ...prev,
  campaignId: undefined,
});

/**
 * Auto-switches the selected campaign to the one named in `?campaignId=<guid>`,
 * then strips the param from the URL. Redirects to /app with a notice when the
 * user lacks access. Platform admins are handled implicitly — `useMyCampaigns`
 * already returns every subsidiary campaign for them.
 */
export function useCampaignDeepLink() {
  const search = useSearch({ strict: false }) as { campaignId?: string };
  const requestedId = search.campaignId;
  const navigate = useNavigate() as unknown as LooseNavigate;
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const selectCampaign = useCampaignStore((s) => s.selectCampaign);
  const { data: myCampaigns, isLoading } = useMyCampaigns();

  useEffect(() => {
    if (!requestedId) return;
    if (isLoading || !myCampaigns) return;

    if (selectedCampaign?.id === requestedId) {
      navigate({ search: STRIP_CAMPAIGN_ID, replace: true });
      return;
    }

    const match = myCampaigns.find((c) => c.id === requestedId);
    if (match) {
      selectCampaign(match);
      navigate({ search: STRIP_CAMPAIGN_ID, replace: true });
    } else {
      navigate({ to: '/app', search: { notice: 'no-access-to-campaign' } });
    }
  }, [requestedId, isLoading, myCampaigns, selectedCampaign?.id, selectCampaign, navigate]);
}
